import { useMapState } from "@/contexts/MapStateContext";
import { ClientEvents, TrackerPosition, Vehicle } from "@/interfaces";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState } from "react";
import { MapContainer, useMap } from "react-leaflet";
import DriverTable from "./DriverTable";
import IconsController from "./IconsController";
import MapTileLayers from "./MapTileLayers";
import VehicleMapController from "./VehicleMapController";

export interface MapProps {
  positions: TrackerPosition[];
  driversWithColors?: Array<{
    name: string;
    color: string;
  }>;
  events?: ClientEvents[];
  vehicles?: Vehicle[];
  showAnimation?: boolean;
  displayEventsAndMarkersOptions?: boolean;
  onVehicleClick?: (vehicleId: number) => void;
}

const MapController = () => {
  const map = useMap();
  const { mapInstance } = useMapState();

  useEffect(() => {
    // Guardar a referência ao mapa
    mapInstance.current = map;

    return () => {
      // Só a referência: `map.off()` aqui removia TODOS os listeners do mapa,
      // inclusive os internos das layers. O renderer SVG (polylines e cercas)
      // é criado uma vez por `map.getRenderer()` e nunca é readicionado, então
      // ficava sem os handlers de zoom/move e os vetores paravam de acompanhar
      // o mapa. O react-leaflet já chama map.remove() ao desmontar.
      if (mapInstance.current === map) {
        mapInstance.current = null;
      }
    };
  }, [map, mapInstance]);

  return null;
};

const MapWithBounds = ({
  positions,
  vehicles,
}: {
  positions: TrackerPosition[];
  vehicles?: Vehicle[];
}) => {
  const map = useMap();
  const { shouldPreserveMapState } = useMapState();
  useEffect(() => {
    if (shouldPreserveMapState.current) {
      return;
    }

    let isMounted = true;
    const timer = setTimeout(() => {
      if (!isMounted) return;

      try {
        const allPositions: L.LatLngTuple[] = [];
        if (positions.length > 0) {
          positions.forEach((point) => {
            allPositions.push([point.latitude, point.longitude]);
          });
        }
        if (vehicles?.length) {
          vehicles.forEach((vehicle) => {
            if (vehicle.last_position) {
              allPositions.push([
                vehicle.last_position.latitude,
                vehicle.last_position.longitude,
              ]);
            }
          });
        }

        if (
          allPositions.length > 0 &&
          map &&
          typeof map.fitBounds === "function"
        ) {
          map.fitBounds(L.latLngBounds(allPositions), {
            padding: [50, 50],
            maxZoom: 13,
            animate: false,
          });
        }
      } catch (error) {
        console.error("Erro ao ajustar os limites do mapa:", error);
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [positions, vehicles, map, shouldPreserveMapState]);

  return null;
};

const Map: React.FC<MapProps> = ({
  positions,
  driversWithColors,
  events,
  vehicles,
  showAnimation = false,
  displayEventsAndMarkersOptions = false,
  onVehicleClick,
}) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState<number>();
  const [zoom] = useState<number>(4);

  return (
    <MapContainer
      center={[-11.59293, -49.597924]}
      zoom={zoom}
      className="w-full h-full z-0 rounded-lg"
      doubleClickZoom={false}
    >
      <MapTileLayers />

      <MapController />
      <MapWithBounds positions={positions} vehicles={vehicles} />

      <IconsController
        events={events}
        setSelectedVehicleId={setSelectedVehicleId}
        vehicles={vehicles}
        positions={positions}
        showAnimation={showAnimation}
        displayEventsAndMarkersOptions={displayEventsAndMarkersOptions}
        onVehicleClick={onVehicleClick}
      />

      <VehicleMapController
        vehicles={vehicles}
        selectedVehicleId={selectedVehicleId}
      />

      {driversWithColors && driversWithColors.length > 0 && (
        <DriverTable driversWithColors={driversWithColors} />
      )}
    </MapContainer>
  );
};

export default React.memo(Map);
